import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/** Dónde se planta el globo en cada sección, y a qué tamaño. */
export interface GlobePosition {
  /** Porcentaje del alto del viewport. */
  top: string;
  /** Porcentaje del ancho del viewport. */
  left: string;
  scale: number;
}

/**
 * Las cuatro paradas del globo, copiadas del sistema actual.
 *
 * La última es "al costado, discreto": centrada tapaba la tarjeta de
 * compra, que es justo la sección donde hay algo que leer y pulsar.
 */
export const DEFAULT_GLOBE_POSITIONS: GlobePositions = [
  { top: "50%", left: "75%", scale: 1.3 },
  { top: "22%", left: "82%", scale: 0.8 },
  { top: "50%", left: "15%", scale: 1.1 },
  { top: "62%", left: "85%", scale: 0.9 },
];

/**
 * Al menos una parada. La tupla no es un capricho de tipos: el globo
 * arranca SIEMPRE en `positions[0]`, así que una lista vacía sería un
 * `transform` indefinido y la pieza pintada en la esquina superior
 * izquierda. Escrito así, ese caso no compila.
 */
export type GlobePositions = readonly [GlobePosition, ...GlobePosition[]];

export interface ScrollGlobeState {
  /** Índice de la sección más cercana al centro del viewport. */
  activeSection: number;
  /** 0 → 1, lo recorrido del documento. Alimenta la barra de progreso. */
  progress: number;
  /** El `transform` del globo, ya calculado. */
  globeTransform: string;
  /** `true` cuando el bloque de secciones ya pasó: el globo se retira. */
  globeHidden: boolean;
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** `ref` que cada sección registra para poder medirse. */
  registerSection: (index: number) => (el: HTMLElement | null) => void;
  /** Lleva la vista a una sección; sin animar si se pidió menos movimiento. */
  scrollToSection: (index: number) => void;
}

const parsePercent = (value: string): number => Number.parseFloat(value.replace("%", ""));

function transformFor(pos: GlobePosition): string {
  return `translate3d(${parsePercent(pos.left)}vw, ${parsePercent(pos.top)}vh, 0) translate3d(-50%, -50%, 0) scale3d(${pos.scale}, ${pos.scale}, 1)`;
}

/**
 * Toda la mecánica de desplazamiento de la landing inmersiva.
 *
 * Vive aparte del componente por una razón práctica: es lo único de
 * `ScrollGlobe` que se puede razonar sin mirar JSX —qué sección manda, a
 * dónde va el globo, cuánto se ha recorrido— y también lo único que toca
 * `window`. Separado, el componente queda siendo lo que dice ser: pintar.
 *
 * ── El listener no se ejecuta en cada píxel ──────────────────────────
 * `scroll` se dispara decenas de veces por segundo y aquí dentro se
 * miden todas las secciones con `getBoundingClientRect`. Sin la compuerta
 * de `requestAnimationFrame`, ese trabajo se haría varias veces por
 * fotograma para pintar una sola vez. Con ella, como mucho una.
 *
 * `{ passive: true }` promete que este listener no va a llamar a
 * `preventDefault`, y a cambio el navegador no tiene que esperar a que
 * termine para desplazar la página.
 *
 * ── Movimiento reducido ──────────────────────────────────────────────
 * `motionOk` no apaga el cálculo entero: la barra de progreso y la
 * sección activa siguen siendo información útil, y la primera la produce
 * el propio desplazamiento de la persona. Lo que sí cambia:
 *
 *   · el globo NO se recoloca — se queda en su primera parada, así que
 *     deja de volar y de escalar de una sección a otra;
 *   · `scrollToSection` salta en vez de deslizar.
 */
export function useScrollGlobe(
  sectionCount: number,
  motionOk: boolean,
  positions: GlobePositions = DEFAULT_GLOBE_POSITIONS,
): ScrollGlobeState {
  const [activeSection, setActiveSection] = useState(0);
  const [progress, setProgress] = useState(0);
  const [globeTransform, setGlobeTransform] = useState(() => transformFor(positions[0]));
  const [globeHidden, setGlobeHidden] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const sectionRefs = useRef<(HTMLElement | null)[]>([]);
  const frameId = useRef<number | undefined>(undefined);

  const registerSection = useCallback(
    (index: number) => (el: HTMLElement | null) => {
      sectionRefs.current[index] = el;
    },
    [],
  );

  const update = useCallback(() => {
    const scrolled = window.scrollY;
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    setProgress(scrollable > 0 ? Math.min(Math.max(scrolled / scrollable, 0), 1) : 0);

    // La sección activa es la que tiene el centro más cerca del centro de
    // la ventana: con secciones de alto de pantalla, es la que se está
    // mirando aunque ninguna empiece exactamente arriba.
    const viewportCenter = window.innerHeight / 2;
    let nearest = 0;
    let minDistance = Number.POSITIVE_INFINITY;

    sectionRefs.current.forEach((el, index) => {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const distance = Math.abs(rect.top + rect.height / 2 - viewportCenter);
      if (distance < minDistance) {
        minDistance = distance;
        nearest = index;
      }
    });

    setActiveSection(nearest);

    // Con movimiento reducido el globo no se mueve de su primera parada:
    // el `transform` inicial se queda como está y no se recalcula.
    if (motionOk) {
      const pos = positions[Math.min(nearest, positions.length - 1)] ?? positions[0];
      setGlobeTransform(transformFor(pos));
    }

    /*
     * El globo es `fixed`: sin este guard se quedaba flotando sobre el pie
     * de la tienda al pasar la última sección. Cuando el final del bloque
     * sube más allá del 80% de la ventana, se retira.
     */
    const container = containerRef.current;
    if (container) {
      setGlobeHidden(container.getBoundingClientRect().bottom < window.innerHeight * 0.8);
    }
  }, [positions, motionOk]);

  useEffect(() => {
    let ticking = false;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      frameId.current = requestAnimationFrame(() => {
        update();
        ticking = false;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    update();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frameId.current !== undefined) cancelAnimationFrame(frameId.current);
    };
    // `sectionCount` entra en las dependencias para que el cálculo se
    // rehaga si cambia el número de secciones (traducción, config nueva).
  }, [update, sectionCount]);

  const scrollToSection = useCallback(
    (index: number) => {
      sectionRefs.current[index]?.scrollIntoView({
        behavior: motionOk ? "smooth" : "auto",
        block: "center",
      });
    },
    [motionOk],
  );

  return useMemo(
    () => ({
      activeSection,
      progress,
      globeTransform,
      globeHidden,
      containerRef,
      registerSection,
      scrollToSection,
    }),
    [
      activeSection,
      progress,
      globeTransform,
      globeHidden,
      registerSection,
      scrollToSection,
    ],
  );
}
